import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useSearch } from '@tanstack/react-router';
import client from '@/api/client';
import type { Hostel } from '@/api/types';
import { Plus, ChevronRight } from 'lucide-react';
import { hostelStatusLabel } from '@/lib/labels';
import { Select } from '@/components/ui/Select';
import { sanitizePhoneInput } from '@/lib/utils';

const STATUS_COLORS = {
  PENDING_APPROVAL: 'bg-amber-100 text-amber-800',
  ACTIVE:           'bg-green-100 text-green-800',
  REJECTED:         'bg-red-100 text-red-700',
  SUSPENDED:        'bg-gray-100 text-gray-700',
};

type HostelStatusFilter = '' | 'ACTIVE' | 'INACTIVE' | 'PENDING_APPROVAL';

export function OwnerHostelsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { status: initialStatus } = useSearch({ from: '/protected/owner/hostels' });
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', address: '', city: '', state: '', pincode: '', phone: '', description: '' });
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<HostelStatusFilter>((initialStatus as HostelStatusFilter) ?? '');

  const { data: hostels = [], isLoading } = useQuery<Hostel[]>({
    queryKey: ['my-hostels'],
    queryFn: () => client.get('/api/hostels/my').then(r => r as unknown as Hostel[]),
  });

  const filteredHostels = hostels.filter(h => {
    if (!statusFilter) return true;
    if (statusFilter === 'INACTIVE') return h.status === 'REJECTED' || h.status === 'SUSPENDED';
    return h.status === statusFilter;
  });

  const createMutation = useMutation({
    mutationFn: (body: typeof form) => client.post('/api/hostels', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['my-hostels'] });
      setShowForm(false);
      setForm({ name: '', address: '', city: '', state: '', pincode: '', phone: '', description: '' });
    },
    onError: (e: Error) => setError(e.message),
  });

  const f = (key: keyof typeof form, label: string, required = false) => (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}{required && ' *'}</label>
      <input
        type={key === 'phone' ? 'tel' : 'text'}
        inputMode={key === 'phone' ? 'tel' : undefined}
        value={form[key]}
        onChange={e => setForm(p => ({ ...p, [key]: key === 'phone' ? sanitizePhoneInput(e.target.value) : e.target.value }))}
        required={required}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
      />
    </div>
  );

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('hostel.title')}</h1>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">{t('common.status')}</label>
            <Select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as HostelStatusFilter)}
              className="w-auto h-9"
              options={[
                { value: '', label: t('dashboard.allHostels') },
                { value: 'ACTIVE', label: t('common.active') },
                { value: 'INACTIVE', label: t('common.inactive') },
                { value: 'PENDING_APPROVAL', label: t('dashboard.pendingActivation') },
              ]}
            />
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="h-9 flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 rounded-lg text-sm font-medium"
          >
            <Plus size={16} /> {t('hostel.addHostel')}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6">
          <h2 className="font-semibold text-gray-800 mb-4">{t('owner.addNewHostel', 'Add New Hostel')}</h2>
          <form
            onSubmit={e => { e.preventDefault(); setError(''); createMutation.mutate(form); }}
            className="grid grid-cols-1 sm:grid-cols-2 gap-4"
          >
            {f('name', t('hostel.hostelName'), true)}
            {f('phone', t('common.phone'))}
            <div className="sm:col-span-2">{f('address', t('common.address'), true)}</div>
            {f('city', t('owner.city', 'City'), true)}
            {f('state', t('owner.state', 'State'), true)}
            {f('pincode', t('owner.pincode', 'Pincode'), true)}
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.description', 'Description')}</label>
              <textarea
                value={form.description}
                onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                rows={2}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
              />
            </div>
            {error && <p className="sm:col-span-2 text-red-600 text-sm">{error}</p>}
            <div className="sm:col-span-2 flex gap-2">
              <button type="submit" disabled={createMutation.isPending} className="bg-teal-600 hover:bg-teal-700 text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
                {createMutation.isPending ? t('owner.submitting', 'Submitting…') : t('owner.submitForApproval', 'Submit for Approval')}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="border px-5 py-2 rounded-lg text-sm">{t('common.cancel')}</button>
            </div>
          </form>
        </div>
      )}

      {isLoading ? <p className="text-gray-400">{t('common.loading')}</p> : filteredHostels.length === 0 ? (
        <p className="text-gray-400 text-center py-10">{t('hostel.noHostelsInCategory')}</p>
      ) : (
        <div className="grid gap-3">
          {filteredHostels.map(h => (
            <div key={h.id} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex items-center">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-medium text-gray-900">{h.name}</p>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[h.status]}`}>
                    {hostelStatusLabel(t, h.status)}
                  </span>
                </div>
                <p className="text-xs text-gray-500">{h.address}, {h.city}, {h.state} {h.pincode}</p>
              </div>
              {h.status === 'ACTIVE' && (
                <Link to="/owner/hostels/$id" params={{ id: h.id }} search={{ bedStatus: '' }} className="ml-4 flex items-center gap-1 text-teal-600 text-sm font-medium hover:underline">
                  {t('common.manage')} <ChevronRight size={14} />
                </Link>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
